import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Form, Row, Col, Card } from 'react-bootstrap';
import parse from 'html-react-parser';
import ms from 'milsymbol';
import { SymbolOptions } from 'milsymbol';
import {
    IDENTITY_OPTIONS,
    SYMBOL_SET_OPTIONS,
    STATUS_OPTIONS,
    ECHELON_OPTIONS,
    HQTFD_OPTIONS,
    LIMITED_STATUS_OPTIONS,
    SIMPLIFIED_HQTFD_OPTIONS,
    buildSidc,
    parseSidc,
    getSubordinateEchelonOptions,
    getUnitTypeGroups,
    getSubgroupsForGroup,
    getUnitTypesForSelection,
    findGroupAndSubgroupForUnitType,
    SidcParts,
    SidcOption
} from './sidcOptions';
import { IconDropdown } from '../../common';
import { createUnitTypeIconRenderer } from './UnitTypeIconRenderer';

export interface SidcBuilderProps {
    /** Current 20-character SIDC code */
    sidc: string;
    /** Callback when SIDC changes */
    onSidcChange: (sidc: string) => void;
    /** Symbol text modifier properties (optional) */
    markerProperties?: SymbolOptions;
    /**
     * The entity's name, used only to render the live preview's symbol label.
     *
     * The label is derived from the name at draw time (as APP-6D
     * `uniqueDesignation`) exactly as the map layers do, so the preview shows
     * what the map will show. It is deliberately NOT part of
     * [markerProperties]: the name has its own column and must not be stored
     * as a text modifier.
     */
    previewName?: string;
    /** Callback when text modifiers change (optional) */
    onMarkerPropertiesChange?: (props: SymbolOptions) => void;
    /**
     * Lock identity to this specific value
     * When set, identity dropdown is disabled and shows "(locked to faction)"
     */
    lockedIdentity?: string;
    /** Disable all form controls (for loading states) */
    disabled?: boolean;
    /**
     * Show text modifiers section
     * @default true
     */
    showTextModifiers?: boolean;
    /**
     * Hide identity dropdown completely (ORBAT creator context)
     * @default false
     */
    hideIdentity?: boolean;
    /**
     * Parent unit's echelon value for filtering echelon options
     * When provided, only echelons smaller than this will be shown
     */
    parentEchelon?: string;
    /**
     * Limit status options to only "Present" and "Planned/Anticipated"
     * @default false
     */
    limitStatusOptions?: boolean;
    /**
     * Replace HQ/TF/Feint/Dummy dropdown with a simple "Dummy" checkbox
     * @default false
     */
    simplifyHqtfdToDummy?: boolean;
    /**
     * Replace Status dropdown with a simple "Planned Unit" checkbox
     * When true: unchecked = '0' (Present), checked = '1' (Planned/Anticipated)
     * @default false
     */
    simplifyStatusToPlanned?: boolean;
}

export default function SidcBuilder({
    sidc,
    onSidcChange,
    markerProperties = {},
    previewName,
    onMarkerPropertiesChange,
    lockedIdentity,
    disabled = false,
    showTextModifiers = true,
    hideIdentity = false,
    parentEchelon,
    limitStatusOptions = false,
    simplifyHqtfdToDummy = false,
    simplifyStatusToPlanned = false
}: SidcBuilderProps) {
    const parts: SidcParts = parseSidc(sidc);

    // State for hierarchical unit type selection
    const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
    const [selectedSubgroup, setSelectedSubgroup] = useState<string | null>(null);

    // Define handleFieldChange first so it can be used in other callbacks
    const handleFieldChange = useCallback((field: keyof SidcParts, value: string) => {
        const newParts = { ...parts, [field]: value };

        // If identity is locked, always use the locked value
        const effectiveIdentity = lockedIdentity ?? newParts.identity;

        const newSidc = buildSidc(
            effectiveIdentity,
            newParts.symbolSet,
            newParts.status,
            newParts.hqtfd,
            newParts.echelon,
            newParts.unitType
        );
        onSidcChange(newSidc);
    }, [parts, lockedIdentity, onSidcChange]);

    // Initialize hierarchical dropdowns when SIDC changes externally (e.g., edit mode)
    useEffect(() => {
        const unitTypeValue = parts.unitType.substring(0, 6);
        const { groupId, subgroupId } = findGroupAndSubgroupForUnitType(unitTypeValue);
        setSelectedGroup(groupId);
        setSelectedSubgroup(subgroupId);
    }, [sidc]);

    // Get available groups
    const groupOptions = useMemo(() => getUnitTypeGroups(), []);

    // Get available subgroups based on selected group
    const subgroupOptions = useMemo(() => {
        return getSubgroupsForGroup(selectedGroup);
    }, [selectedGroup]);

    // Get available unit types based on selected group and subgroup
    const filteredUnitTypes = useMemo(() => {
        return getUnitTypesForSelection(selectedGroup, selectedSubgroup);
    }, [selectedGroup, selectedSubgroup]);

    // Handle group change - reset subgroup and unit type
    const handleGroupChange = useCallback((groupId: string) => {
        const newGroupId = groupId === 'unspecified' ? null : groupId;
        setSelectedGroup(newGroupId);
        setSelectedSubgroup(null);

        // Get the first available unit type for this group (with no subgroup)
        const availableTypes = getUnitTypesForSelection(newGroupId, null);
        if (availableTypes.length > 0) {
            handleFieldChange('unitType', availableTypes[0].value.padEnd(10, '0'));
        }
    }, [handleFieldChange]);

    // Handle subgroup change - reset unit type
    const handleSubgroupChange = useCallback((subgroupId: string) => {
        const newSubgroupId = subgroupId === '' ? null : subgroupId;
        setSelectedSubgroup(newSubgroupId);

        // Get the first available unit type for this subgroup
        const availableTypes = getUnitTypesForSelection(selectedGroup, newSubgroupId);
        if (availableTypes.length > 0) {
            handleFieldChange('unitType', availableTypes[0].value.padEnd(10, '0'));
        }
    }, [selectedGroup, handleFieldChange]);

    const handleTextChange = (field: keyof SymbolOptions, value: string) => {
        if (onMarkerPropertiesChange) {
            onMarkerPropertiesChange({ ...markerProperties, [field]: value || '' });
        }
    };

    // Generate live preview symbol
    const previewSymbol = useMemo(() => {
        const symbol = new ms.Symbol(sidc, {
            size: 80,
            ...markerProperties,
            ...(previewName ? { uniqueDesignation: previewName } : {}),
        });
        return symbol.asSVG();
    }, [sidc, markerProperties, previewName]);

    // Effective identity value (locked or from SIDC parts)
    const identityValue = lockedIdentity ?? parts.identity;
    const isIdentityLocked = !!lockedIdentity;

    // Memoized filtered options based on context
    const echelonOptions: SidcOption[] = useMemo(() => {
        if (parentEchelon) {
            return getSubordinateEchelonOptions(parentEchelon);
        }
        return ECHELON_OPTIONS;
    }, [parentEchelon]);

    const statusOptions: SidcOption[] = useMemo(() => {
        return limitStatusOptions ? LIMITED_STATUS_OPTIONS : STATUS_OPTIONS;
    }, [limitStatusOptions]);

    const hqtfdOptions: SidcOption[] = useMemo(() => {
        return simplifyHqtfdToDummy ? SIMPLIFIED_HQTFD_OPTIONS : HQTFD_OPTIONS;
    }, [simplifyHqtfdToDummy]);

    // Memoized icon renderer that uses current SIDC context
    // Icons update when identity, symbolSet, status, hqtfd, or echelon changes
    const unitTypeIconRenderer = useMemo(
        () => createUnitTypeIconRenderer(parts),
        [parts.identity, parts.symbolSet, parts.status, parts.hqtfd, parts.echelon]
    );

    // Handler for unit type selection from IconDropdown
    const handleUnitTypeChange = useCallback(
        (value: string | number) => {
            handleFieldChange('unitType', String(value).padEnd(10, '0'));
        },
        [handleFieldChange]
    );

    // Handle dummy checkbox change (for simplified HQTFD mode)
    const handleDummyCheckboxChange = (checked: boolean) => {
        // '1' = Feint/Dummy, '0' = None (normal unit)
        handleFieldChange('hqtfd', checked ? '1' : '0');
    };

    // Handle planned checkbox change (for simplified Status mode)
    const handlePlannedCheckboxChange = (checked: boolean) => {
        // '1' = Planned/Anticipated, '0' = Present
        handleFieldChange('status', checked ? '1' : '0');
    };

    // Determine if unit is dummy based on current HQTFD value
    // Values with Feint/Dummy: 1, 3, 5, 7 (odd numbers)
    const isDummy = parseInt(parts.hqtfd, 10) % 2 === 1;

    // Determine if unit is planned based on current status value
    const isPlanned = parts.status === '1';

    return (
        <div className="sidc-builder">
            {/* Symbol Preview */}
            <Card className="mb-3 text-center">
                <Card.Body>
                    {parse(previewSymbol)}
                    <div className="mt-2"><code>{sidc}</code></div>
                </Card.Body>
            </Card>

            {/* Symbol Configuration Header */}
            <h6 className="mt-4 mb-3">Symbol Configuration</h6>

            {/* Core Fields */}
            <Row className="mb-2">
                {/* Identity field - conditionally hidden for ORBAT context */}
                {!hideIdentity && (
                    <Col sm={6}>
                        <Form.Group>
                            <Form.Label>
                                Identity
                                {isIdentityLocked && (
                                    <span className="text-muted ms-2">(locked to faction)</span>
                                )}
                            </Form.Label>
                            <Form.Select
                                value={identityValue}
                                onChange={(e) => handleFieldChange('identity', e.target.value)}
                                disabled={disabled || isIdentityLocked}
                            >
                                {IDENTITY_OPTIONS.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
                <Col sm={hideIdentity ? 12 : 6}>
                    <Form.Group>
                        <Form.Label>Symbol Set</Form.Label>
                        <Form.Select
                            value={parts.symbolSet}
                            onChange={(e) => handleFieldChange('symbolSet', e.target.value)}
                            disabled={disabled}
                        >
                            {SYMBOL_SET_OPTIONS.map(opt => (
                                <option key={opt.value} value={opt.value}>{opt.label}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            {/* Hierarchical Unit Type Selection */}
            <Row className="mb-2">
                <Col sm={6}>
                    <Form.Group>
                        <Form.Label>Group</Form.Label>
                        <Form.Select
                            value={selectedGroup || 'unspecified'}
                            onChange={(e) => handleGroupChange(e.target.value)}
                            disabled={disabled}
                        >
                            {groupOptions.map(group => (
                                <option key={group.id} value={group.id}>{group.groupName}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
                <Col sm={6}>
                    <Form.Group>
                        <Form.Label>Subset</Form.Label>
                        <Form.Select
                            value={selectedSubgroup || ''}
                            onChange={(e) => handleSubgroupChange(e.target.value)}
                            disabled={disabled || subgroupOptions.length === 0}
                        >
                            <option value="">-- Direct Members --</option>
                            {subgroupOptions.map(subgroup => (
                                <option key={subgroup.id} value={subgroup.id}>{subgroup.groupName}</option>
                            ))}
                        </Form.Select>
                    </Form.Group>
                </Col>
            </Row>

            <Form.Group className="mb-2">
                <Form.Label>Unit Type</Form.Label>
                <IconDropdown
                    options={filteredUnitTypes}
                    value={parts.unitType.substring(0, 6)}
                    onChange={handleUnitTypeChange}
                    getOptionValue={(opt) => opt.value}
                    getOptionLabel={(opt) => opt.label}
                    renderIcon={unitTypeIconRenderer}
                    disabled={disabled}
                    ariaLabel="Select unit type"
                    placeholder="Select unit type"
                    maxHeight="400px"
                    testId="unit-type-dropdown"
                />
            </Form.Group>

            <Row className="mb-2">
                {/* Status - either as dropdown or simplified checkbox */}
                {simplifyStatusToPlanned ? (
                    <Col sm={6}>
                        <Form.Group>
                            <Form.Label>Echelon</Form.Label>
                            <Form.Select
                                value={parts.echelon}
                                onChange={(e) => handleFieldChange('echelon', e.target.value)}
                                disabled={disabled}
                            >
                                {echelonOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                ) : (
                    <>
                        <Col sm={simplifyHqtfdToDummy ? 6 : 4}>
                            <Form.Group>
                                <Form.Label>Status</Form.Label>
                                <Form.Select
                                    value={parts.status}
                                    onChange={(e) => handleFieldChange('status', e.target.value)}
                                    disabled={disabled}
                                >
                                    {statusOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                        <Col sm={simplifyHqtfdToDummy ? 6 : 4}>
                            <Form.Group>
                                <Form.Label>Echelon</Form.Label>
                                <Form.Select
                                    value={parts.echelon}
                                    onChange={(e) => handleFieldChange('echelon', e.target.value)}
                                    disabled={disabled}
                                >
                                    {echelonOptions.map(opt => (
                                        <option key={opt.value} value={opt.value}>{opt.label}</option>
                                    ))}
                                </Form.Select>
                            </Form.Group>
                        </Col>
                    </>
                )}
                {/* HQ/TF/Feint/Dummy - either as dropdown or simplified checkbox */}
                {simplifyHqtfdToDummy ? (
                    <Col sm={simplifyStatusToPlanned ? 6 : 12} className={simplifyStatusToPlanned ? '' : 'mt-2'}>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                id="dummy-unit-checkbox"
                                label="Dummy Unit"
                                checked={isDummy}
                                onChange={(e) => handleDummyCheckboxChange(e.target.checked)}
                                disabled={disabled}
                            />
                            <Form.Text className="text-muted">
                                Check this box if this unit represents a dummy/decoy force
                            </Form.Text>
                        </Form.Group>
                    </Col>
                ) : (
                    <Col sm={4}>
                        <Form.Group>
                            <Form.Label>HQ/TF/Feint/Dummy</Form.Label>
                            <Form.Select
                                value={parts.hqtfd}
                                onChange={(e) => handleFieldChange('hqtfd', e.target.value)}
                                disabled={disabled}
                            >
                                {hqtfdOptions.map(opt => (
                                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    </Col>
                )}
            </Row>
            {/* Planned Unit checkbox - shown when simplifyStatusToPlanned is true */}
            {simplifyStatusToPlanned && (
                <Row className="mb-2">
                    <Col sm={12}>
                        <Form.Group>
                            <Form.Check
                                type="checkbox"
                                id="planned-unit-checkbox"
                                label="Planned Unit"
                                checked={isPlanned}
                                onChange={(e) => handlePlannedCheckboxChange(e.target.checked)}
                                disabled={disabled}
                            />
                            <Form.Text className="text-muted">
                                Mark this unit as planned/anticipated rather than present
                            </Form.Text>
                        </Form.Group>
                    </Col>
                </Row>
            )}

            {/* Text Modifiers Section */}
            {showTextModifiers && onMarkerPropertiesChange && (
                <>
                    <hr />
                    <h6>Text Modifiers</h6>

                    <Form.Group className="mb-2">
                        <Form.Label>Common Identifier</Form.Label>
                        <Form.Control
                            type="text"
                            placeholder="e.g., Hawk"
                            value={markerProperties.commonIdentifier || ''}
                            onChange={(e) => handleTextChange('commonIdentifier', e.target.value)}
                            disabled={disabled}
                        />
                        <Form.Text className="text-muted">
                            APP-6D field AF — the popular name of the equipment type
                            (&quot;Hawk&quot;, &quot;Mavic&quot;). Not the entity&apos;s name: that is the
                            Name field above, which is what gets drawn as the symbol&apos;s
                            designation label.
                        </Form.Text>
                    </Form.Group>

                    <Row className="mb-2">
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Higher Formation</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., 1BDE"
                                    value={markerProperties.higherFormation || ''}
                                    onChange={(e) => handleTextChange('higherFormation', e.target.value)}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>
                        <Col sm={6}>
                            <Form.Group>
                                <Form.Label>Special HQ</Form.Label>
                                <Form.Control
                                    type="text"
                                    placeholder="e.g., ISAF"
                                    value={markerProperties.specialHeadquarters || ''}
                                    onChange={(e) => handleTextChange('specialHeadquarters', e.target.value)}
                                    disabled={disabled}
                                />
                            </Form.Group>
                        </Col>
                    </Row>
                </>
            )}
        </div>
    );
}
